import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ChevronDown, ChevronLeft, ChevronRight, Pencil, Trash2 } from 'lucide-react'
import { api } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { CardCifra } from '../../components/ui/CardCifra'
import { Pastila } from '../../components/ui/Pastila'
import { StareLista } from '../../components/ui/StareLista'
import { useConfirmare } from '../../components/ui/Confirmare'
import type { Ton } from '../../components/ui/TonuriB4L'
import {
  ListaDocumente,
  STARE,
  citesteSuma,
  lei,
  mesajEroare,
  zi,
  type DocLista,
  type StareObligatie,
} from './taxeComune'

/*
  Bani → Taxe & impozite → Impozit pe profit (faza 2, 23.10.2026). Trimestrial, cumulat de la
  01.01. Trimestrul inchis (are `691 = 441` in note) arata suma din balanta; cel deschis, estimarea.
*/

type TipAjustare = 'venit' | 'cheltuiala' | 'nedeductibil'

interface Rand {
  cod: string
  eticheta: string
  sursa: string
  fel: 'contabilitate' | 'estimat' | 'manual' | 'calcul'
  semn: '+' | '−' | '=' | ''
  nivel: 0 | 1
  total: number
  documente: DocLista[]
  deVerificat?: DocLista[]
  nota?: string
}
interface AjustareProfit {
  id: number
  tip: TipAjustare
  descriere: string
  suma: number
  cont: string | null
  autor: string
  data: string
}
interface EstimareProfit {
  perioada: string
  termen: string | null
  inchis: boolean
  suma: number
  stare: StareObligatie
  balanta: { declarat: number; sold: number; platit: number; documente: DocLista[] }
  calcul: { deplata: number; datoratCumulat: number; impozitBrut: number }
  randuri: Rand[]
  ajustari: AjustareProfit[]
  copieIncompleta: string | null
}

const TIPURI: { cheie: TipAjustare; eticheta: string }[] = [
  { cheie: 'venit', eticheta: 'Venit' },
  { cheie: 'cheltuiala', eticheta: 'Cheltuială' },
  { cheie: 'nedeductibil', eticheta: 'Nedeductibil' },
]
const FEL: Record<Rand['fel'], { eticheta: string; ton: Ton } | null> = {
  contabilitate: { eticheta: 'din contabilitate', ton: 'gri' },
  estimat: { eticheta: 'estimat din CRM', ton: 'mov' },
  manual: { eticheta: 'manual', ton: 'galben' },
  calcul: null,
}

/** Trimestrul implicit: cel incheiat — pe el se depune D100 acum. */
export function trimestruImplicit(): string {
  const a = new Date()
  const t = Math.floor(a.getMonth() / 3) // trimestrul curent, de la 0
  return t === 0 ? `${a.getFullYear() - 1}-T4` : `${a.getFullYear()}-T${t}`
}
function mutaTrimestru(p: string, cu: number): string {
  const i = Number(p.slice(0, 4)) * 4 + Number(p.slice(6)) - 1 + cu
  return `${Math.floor(i / 4)}-T${(i % 4) + 1}`
}
const numeTrimestru = (p: string) => `trimestrul ${p.slice(6)} ${p.slice(0, 4)}`

export function ImpozitProfit({
  trimestru,
  setTrimestru,
  eAdmin,
}: {
  trimestru: string
  setTrimestru: (t: string) => void
  eAdmin: boolean
}) {
  const q = useQuery<EstimareProfit>({
    queryKey: ['taxe-profit', trimestru],
    queryFn: async () => (await api.get('/taxe/profit', { params: { perioada: trimestru } })).data,
  })
  const an = Number(trimestru.slice(0, 4))
  const ani = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 3 + i)
  if (!ani.includes(an)) ani.push(an)
  const d = q.data

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 text-corp">
        <Button variant="secondary" onClick={() => setTrimestru(mutaTrimestru(trimestru, -1))}>
          <ChevronLeft size={14} />
          <span className="sr-only">Trimestrul precedent</span>
        </Button>
        <select
          value={an}
          onChange={(e) => setTrimestru(`${e.target.value}-${trimestru.slice(5)}`)}
          aria-label="Anul"
          className="rounded-caseta border border-b4l-border bg-b4l-surface px-2 py-1.5 text-corp"
        >
          {ani.sort().map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <select
          value={trimestru.slice(5)}
          onChange={(e) => setTrimestru(`${an}-${e.target.value}`)}
          aria-label="Trimestrul"
          className="rounded-caseta border border-b4l-border bg-b4l-surface px-2 py-1.5 text-corp"
        >
          {['T1', 'T2', 'T3', 'T4'].map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <Button variant="secondary" onClick={() => setTrimestru(mutaTrimestru(trimestru, 1))}>
          <ChevronRight size={14} />
          <span className="sr-only">Trimestrul următor</span>
        </Button>
        <span className="text-b4l-text-muted">
          Impozit pe profit, {numeTrimestru(trimestru)} — cumulat de la 01.01.{an}
        </span>
      </div>

      <StareLista interogare={q} gol={false} mesajGol="">
        {d && (
          <>
            {d.copieIncompleta && (
              <div className="rounded-caseta border border-b4l-amber bg-b4l-amber-light px-3.5 py-2.5 text-corp text-b4l-amber-dark">
                {d.copieIncompleta}
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-3">
              <CardCifra
                ton={d.inchis ? 'gri' : d.suma >= 0 ? 'albastru' : 'verde'}
                eticheta={
                  d.inchis
                    ? 'Impozit declarat (din 441)'
                    : d.suma >= 0
                      ? 'Impozit de plată estimat'
                      : 'Impozit de recuperat estimat'
                }
                valoare={`${lei(d.suma)} lei`}
                detaliu={
                  <>
                    Termen: {d.termen ? zi(d.termen) : 'prin declarația anuală (D101)'} ·{' '}
                    <Pastila ton={STARE[d.stare].ton}>{STARE[d.stare].eticheta}</Pastila>
                  </>
                }
                explicatie={
                  d.inchis
                    ? 'Trimestrul are impozitul înregistrat în contabilitate (691 = 441), deci suma e cea din balanță, nu estimarea.'
                    : 'Impozit datorat cumulat de la 01.01 minus ce s-a înregistrat în trimestrele anterioare. Termenul e 25 ale lunii de după trimestru, mutat pe prima zi lucrătoare.'
                }
              />
              <CardCifra
                ton="gri"
                eticheta="Sold 441 la sfârșitul trimestrului"
                valoare={`${lei(d.balanta.sold)} lei`}
                detaliu={
                  d.inchis
                    ? `Plătit din bancă după trimestru: ${lei(d.balanta.platit)} lei`
                    : 'Din notele contabile, toate analiticele 441'
                }
              />
              <CardCifra
                ton="gri"
                eticheta={d.inchis ? 'Estimarea, pentru comparație' : 'Impozit datorat cumulat'}
                valoare={`${lei(d.inchis ? d.calcul.deplata : d.calcul.datoratCumulat)} lei`}
                detaliu={
                  d.inchis
                    ? `Diferență față de 441: ${lei(d.calcul.deplata - d.suma)} lei`
                    : `Impozit 16%: ${lei(d.calcul.impozitBrut)} lei, minus creditul de sponsorizare`
                }
              />
            </div>

            {d.inchis ? (
              <>
                <section className="grid gap-2 rounded-xl border border-b4l-border bg-b4l-surface p-3.5">
                  <h2 className="m-0 text-base font-semibold">Din balanță — contul 441</h2>
                  <p className="m-0 text-mic text-b4l-text-muted">
                    Impozitul înregistrat în trimestru (691 = 441) și plățile din bancă de după
                    trimestru.
                  </p>
                  {d.balanta.documente.length > 0 && <ListaDocumente docs={d.balanta.documente} />}
                </section>
                <details className="rounded-xl border border-b4l-border bg-b4l-surface p-3.5">
                  <summary className="cursor-pointer font-medium">
                    Calculul estimat, pentru comparație
                  </summary>
                  <div className="mt-3">
                    <TabelProfit randuri={d.randuri} />
                  </div>
                </details>
              </>
            ) : (
              <TabelProfit randuri={d.randuri} />
            )}
            {eAdmin && <AjustariProfit trimestru={trimestru} ajustari={d.ajustari} />}
          </>
        )}
      </StareLista>
    </>
  )
}

function TabelProfit({ randuri }: { randuri: Rand[] }) {
  const [deschis, setDeschis] = useState<string | null>(null)
  return (
    <div className="overflow-x-auto rounded-xl border border-b4l-border bg-b4l-surface">
      <table className="w-full min-w-[760px] border-collapse text-corp">
        <thead>
          <tr className="text-left text-mic text-b4l-text-muted">
            <th className="border-b border-b4l-border px-3.5 py-2.5 font-medium">Rând</th>
            <th className="border-b border-b4l-border px-2 py-2.5 font-medium">Ce</th>
            <th className="border-b border-b4l-border px-2 py-2.5 font-medium">Sursa</th>
            <th className="border-b border-b4l-border px-3.5 py-2.5 text-right font-medium">
              Suma (lei)
            </th>
          </tr>
        </thead>
        <tbody>
          {randuri.map((r) => {
            const e = deschis === r.cod
            const arePeCe = r.documente.length > 0 || !!r.deVerificat?.length || !!r.nota
            const fel = FEL[r.fel]
            return (
              <Fragment key={r.cod}>
                <tr
                  onClick={() => arePeCe && setDeschis(e ? null : r.cod)}
                  className={`${arePeCe ? 'cursor-pointer hover:bg-b4l-bg-soft' : ''} ${
                    r.nivel === 0 ? 'font-semibold' : ''
                  }`}
                >
                  <td className="border-b border-b4l-border px-3.5 py-2">
                    <span className={`inline-flex items-center gap-1 ${r.nivel ? 'pl-3' : ''}`}>
                      {arePeCe && (
                        <ChevronDown
                          size={14}
                          className={`transition ${e ? '' : '-rotate-90'}`}
                          aria-hidden
                        />
                      )}
                      {r.cod}
                    </span>
                  </td>
                  <td className="border-b border-b4l-border px-2 py-2">
                    <span className={r.nivel ? 'pl-3' : ''}>{r.eticheta}</span>{' '}
                    {fel && <Pastila ton={fel.ton}>{fel.eticheta}</Pastila>}
                    {!!r.deVerificat?.length && (
                      <span className="ml-1">
                        <Pastila ton="galben">{r.deVerificat.length} de verificat</Pastila>
                      </span>
                    )}
                  </td>
                  <td className="border-b border-b4l-border px-2 py-2 text-mic font-normal text-b4l-text-muted">
                    {r.sursa}
                  </td>
                  <td className="border-b border-b4l-border px-3.5 py-2 text-right tabular-nums">
                    {r.semn && r.semn !== '=' ? `${r.semn} ` : ''}
                    {lei(r.total)}
                  </td>
                </tr>
                {e && (
                  <tr>
                    <td
                      colSpan={4}
                      className="border-b border-b4l-border bg-b4l-bg-soft px-3.5 py-3"
                    >
                      {r.nota && <p className="m-0 mb-2 text-mic text-b4l-text-muted">{r.nota}</p>}
                      {r.documente.length > 0 && <ListaDocumente docs={r.documente} />}
                      {!!r.deVerificat?.length && (
                        <div className="mt-3">
                          <p className="m-0 mb-1 text-mic font-semibold text-b4l-amber-dark">
                            De verificat — nu intră în sumă
                          </p>
                          <ListaDocumente docs={r.deVerificat} />
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function AjustariProfit({
  trimestru,
  ajustari,
}: {
  trimestru: string
  ajustari: AjustareProfit[]
}) {
  const qc = useQueryClient()
  const { confirma } = useConfirmare()
  const [editez, setEditez] = useState<number | null>(null)
  const [tip, setTip] = useState<TipAjustare>('cheltuiala')
  const [descriere, setDescriere] = useState('')
  const [suma, setSuma] = useState('')
  const [cont, setCont] = useState('')
  const goleste = () => {
    setEditez(null)
    setTip('cheltuiala')
    setDescriere('')
    setSuma('')
    setCont('')
  }
  const reincarca = () => qc.invalidateQueries({ queryKey: ['taxe-profit', trimestru] })
  const salveaza = useMutation({
    mutationFn: async () => {
      const s = citesteSuma(suma)
      if (s == null || Number.isNaN(s)) throw new Error('Suma nu e un număr.')
      const body = { tip, descriere: descriere.trim(), suma: s, cont: cont.trim() || null }
      if (editez != null) await api.put(`/taxe/ajustari/${editez}`, body)
      else await api.post('/taxe/ajustari', { ...body, obligatie: 'profit', perioada: trimestru })
    },
    onSuccess: () => {
      toast.success(editez != null ? 'Ajustarea a fost modificată.' : 'Ajustarea a fost adăugată.')
      goleste()
      void reincarca()
    },
    onError: (e) =>
      toast.error(e instanceof Error && !('response' in e) ? e.message : mesajEroare(e)),
  })
  const sterge = useMutation({
    mutationFn: async (id: number) => api.delete(`/taxe/ajustari/${id}`),
    onSuccess: () => {
      toast.success('Ajustarea a fost ștearsă.')
      void reincarca()
    },
    onError: (e) => toast.error(mesajEroare(e)),
  })
  const camp = 'rounded-caseta border border-b4l-border bg-b4l-surface px-3 py-1.5 text-corp'

  return (
    <section className="grid gap-3 rounded-xl border border-b4l-border bg-b4l-surface p-3.5">
      <div>
        <h2 className="m-0 text-base font-semibold">Ajustări manuale</h2>
        <p className="m-0 text-mic text-b4l-text-muted">
          Judecată contabilă care nu reiese din documente: vânzări sau cost de marfă neînregistrate,
          amortizare neînregistrată, facturi externe (Nextcloud) încă neintrate în ExpertAccounts.
          „Venit” și „Cheltuială” se adună la rândul lor (negativ scade); „Nedeductibil” crește doar
          baza impozabilă. O cheltuială pe cont 6584 (sponsorizare), 6581 sau 6811…neded intră și la
          nedeductibile.
        </p>
      </div>
      {ajustari.length > 0 && (
        <ul className="m-0 grid gap-2 p-0">
          {ajustari.map((a) => (
            <li
              key={a.id}
              className="flex list-none flex-wrap items-center gap-2 rounded-caseta border border-b4l-border px-3 py-2"
            >
              <Pastila ton="galben">{TIPURI.find((t) => t.cheie === a.tip)?.eticheta}</Pastila>
              <div className="min-w-0 flex-1">
                <div className="font-medium">{a.descriere}</div>
                <div className="text-mic text-b4l-text-muted">
                  {a.cont ? `cont ${a.cont} · ` : ''}
                  {a.autor}, {new Date(a.data).toLocaleString('ro-RO')}
                </div>
              </div>
              <span className="tabular-nums">{lei(a.suma)} lei</span>
              <Button
                variant="ghost"
                onClick={() => {
                  setEditez(a.id)
                  setTip(a.tip)
                  setDescriere(a.descriere)
                  setSuma(String(a.suma).replace('.', ','))
                  setCont(a.cont ?? '')
                }}
              >
                <Pencil size={14} />
                <span className="sr-only">Modifică</span>
              </Button>
              <Button
                variant="ghost"
                onClick={async () => {
                  if (
                    await confirma({
                      titlu: 'Ștergi ajustarea?',
                      mesaj: a.descriere,
                      buton: 'Șterge ajustarea',
                      pericol: true,
                    })
                  )
                    sterge.mutate(a.id)
                }}
              >
                <Trash2 size={14} />
                <span className="sr-only">Șterge</span>
              </Button>
            </li>
          ))}
        </ul>
      )}
      <form
        className="grid gap-2 sm:grid-cols-[140px_1fr_140px_120px_auto]"
        onSubmit={(e) => {
          e.preventDefault()
          salveaza.mutate()
        }}
      >
        <select
          value={tip}
          onChange={(e) => setTip(e.target.value as TipAjustare)}
          aria-label="Tip"
          className={camp}
        >
          {TIPURI.map((t) => (
            <option key={t.cheie} value={t.cheie}>
              {t.eticheta}
            </option>
          ))}
        </select>
        <input
          value={descriere}
          onChange={(e) => setDescriere(e.target.value)}
          placeholder="Explicație (ex. vânzări Amazon septembrie neînregistrate)"
          aria-label="Explicație"
          className={camp}
        />
        <input
          value={suma}
          onChange={(e) => setSuma(e.target.value)}
          placeholder="Suma (lei)"
          aria-label="Suma"
          inputMode="decimal"
          className={`${camp} text-right tabular-nums`}
        />
        <input
          value={cont}
          onChange={(e) => setCont(e.target.value)}
          placeholder="Cont (opțional)"
          aria-label="Cont"
          className={camp}
        />
        <div className="flex gap-2">
          <Button
            type="submit"
            loading={salveaza.isPending}
            disabled={descriere.trim().length < 3 || !suma.trim()}
          >
            {editez != null ? 'Salvează' : 'Adaugă'}
          </Button>
          {editez != null && (
            <Button variant="secondary" onClick={goleste}>
              Renunță
            </Button>
          )}
        </div>
      </form>
    </section>
  )
}
